import { PlusIcon } from '@langchain/untitled-ui-icons';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SecretInput } from '@/components/SecretInput';
import { useTenantSecrets, useTenantSecretsMutation } from '@/hooks/useSwr';

interface WorkspaceSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (secretKey: string) => void;
}

interface CreateSecretFormData {
  key: string;
  value: string;
}

export function WorkspaceSecretDialog({
  open,
  onOpenChange,
  onSubmit,
}: WorkspaceSecretDialogProps) {
  'use no memo';
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { data: secrets, isLoading } = useTenantSecrets();
  const form = useForm<CreateSecretFormData>();
  const createSecretMutation = useTenantSecretsMutation({
    onSuccess: () => {
      const newSecretKey = form.getValues('key');
      setShowCreateForm(false);
      form.reset();
      onSubmit(newSecretKey);
      onOpenChange(false);
    },
  });

  // Sort secrets with PagerDuty-related keys at the top
  const sortedSecrets = useMemo(() => {
    if (!secrets) return [];

    const priorityKeywords = ['pagerduty', 'pager', 'duty', 'integration'];

    return [...secrets].sort((a, b) => {
      const aHasPriority = priorityKeywords.some((keyword) =>
        a.key.toLowerCase().includes(keyword)
      );
      const bHasPriority = priorityKeywords.some((keyword) =>
        b.key.toLowerCase().includes(keyword)
      );

      if (aHasPriority && !bHasPriority) return -1;
      if (!aHasPriority && bHasPriority) return 1;
      return a.key.localeCompare(b.key);
    });
  }, [secrets]);

  const handleSecretSelect = (secretKey: string) => {
    onSubmit(secretKey);
    onOpenChange(false);
  };

  const handleCreateSecret = (data: CreateSecretFormData) => {
    createSecretMutation.trigger({
      json: [{ key: data.key, value: data.value }],
    });
  };

  const handleCancel = () => {
    if (showCreateForm) {
      setShowCreateForm(false);
      form.reset();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={showCreateForm ? 'Create New Secret' : 'Select Workspace Secret'}
        className="w-[500px]"
      >
        {showCreateForm ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(handleCreateSecret)}
          >
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-primary">Name</label>
              <input
                type="text"
                placeholder="Enter secret name"
                className="rounded-md border border-secondary bg-transparent px-3 py-2 font-mono text-sm focus:border-brand focus:outline-none"
                {...form.register('key', { required: true })}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-primary">Value</label>
              <SecretInput
                placeholder="Enter secret value"
                {...form.register('value', { required: true })}
              />
            </div>

            {createSecretMutation.error && (
              <ExpandableErrorAlert error={createSecretMutation.error} />
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-secondary bg-transparent px-3 py-1.5 text-sm font-medium text-primary hover:bg-secondary-hover disabled:pointer-events-none disabled:opacity-50"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-button-primary hover:bg-brand-primary-hover disabled:pointer-events-none disabled:opacity-50"
                disabled={createSecretMutation.isMutating}
              >
                {createSecretMutation.isMutating
                  ? 'Creating...'
                  : 'Create & Select'}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            {isLoading ? (
              <div className="py-4 text-center">Loading secrets...</div>
            ) : sortedSecrets.length === 0 ? (
              // Empty state when no secrets exist
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="text-center">
                  <h3 className="mb-2 text-lg font-medium text-primary">
                    No workspace secrets found
                  </h3>
                  <p className="mb-4 text-sm text-secondary">
                    Create your first workspace secret to securely store
                    integration keys and other sensitive values.
                  </p>
                </div>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md bg-brand-primary px-4 py-2 text-sm font-medium text-button-primary hover:bg-brand-primary-hover disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => setShowCreateForm(true)}
                >
                  <PlusIcon className="h-4 w-4" />
                  Create Your First Secret
                </button>
              </div>
            ) : (
              <>
                <div className="max-h-[300px] overflow-y-auto">
                  <div className="space-y-2">
                    {sortedSecrets.map((secret) => (
                      <button
                        key={secret.key}
                        type="button"
                        className="w-full rounded-md border border-secondary p-3 text-left transition-colors hover:bg-secondary-hover"
                        onClick={() => handleSecretSelect(secret.key)}
                      >
                        <div className="font-mono text-sm">{secret.key}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-secondary pt-4">
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-secondary bg-transparent px-3 py-1.5 text-sm font-medium text-primary hover:bg-secondary-hover disabled:pointer-events-none disabled:opacity-50"
                    onClick={() => setShowCreateForm(true)}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create New Secret
                  </button>
                </div>
              </>
            )}

            {sortedSecrets.length > 0 && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-secondary bg-transparent px-3 py-1.5 text-sm font-medium text-primary hover:bg-secondary-hover disabled:pointer-events-none disabled:opacity-50"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
